/**
 * Pinball Game
 * Classic pinball with ball physics and flippers
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Pinball = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('pinball_score', 0);
  const [highScore, setHighScore] = useLocalStorage('pinball_highScore', 0);
  const [balls, setBalls] = useState(3);

  const gameStateRef = useRef({
    ball: { x: 150, y: 50, vx: 0, vy: 0, radius: 8 },
    leftFlipper: { x: 60, y: 430, length: 80, angle: 0, baseAngle: 0.3 },
    rightFlipper: { x: 240, y: 430, length: 80, angle: 0, baseAngle: -0.3 },
    bumpers: [
      { x: 80, y: 150, radius: 20, hits: 0 },
      { x: 220, y: 150, radius: 20, hits: 0 },
      { x: 150, y: 250, radius: 20, hits: 0 },
      { x: 100, y: 350, radius: 20, hits: 0 },
      { x: 200, y: 350, radius: 20, hits: 0 },
    ],
    ballActive: true,
  });

  const CANVAS_WIDTH = 300;
  const CANVAS_HEIGHT = 500;
  const GRAVITY = 0.3;
  const BUMPER_BOOST = 15;

  const startGame = () => {
    gameStateRef.current = {
      ball: { x: 150, y: 50, vx: 0, vy: 0, radius: 8 },
      leftFlipper: { x: 60, y: 430, length: 80, angle: 0, baseAngle: 0.3 },
      rightFlipper: { x: 240, y: 430, length: 80, angle: 0, baseAngle: -0.3 },
      bumpers: [
        { x: 80, y: 150, radius: 20, hits: 0 },
        { x: 220, y: 150, radius: 20, hits: 0 },
        { x: 150, y: 250, radius: 20, hits: 0 },
        { x: 100, y: 350, radius: 20, hits: 0 },
        { x: 200, y: 350, radius: 20, hits: 0 },
      ],
      ballActive: true,
    };
    setBalls(3);
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const keysPressed = {};

    const handleKeyDown = (e) => {
      if (e.key === 'z' || e.key === 'Z') keysPressed['left'] = true;
      if (e.key === 'm' || e.key === 'M') keysPressed['right'] = true;
    };

    const handleKeyUp = (e) => {
      if (e.key === 'z' || e.key === 'Z') keysPressed['left'] = false;
      if (e.key === 'm' || e.key === 'M') keysPressed['right'] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const gameLoop = setInterval(() => {
      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Flipper control
      game.leftFlipper.angle = keysPressed['left'] ? game.leftFlipper.baseAngle + 0.5 : game.leftFlipper.baseAngle;
      game.rightFlipper.angle = keysPressed['right'] ? game.rightFlipper.baseAngle - 0.5 : game.rightFlipper.baseAngle;

      // Ball physics
      game.ball.vy += GRAVITY;
      game.ball.x += game.ball.vx;
      game.ball.y += game.ball.vy;

      // Wall collisions
      if (game.ball.x - game.ball.radius < 0) {
        game.ball.x = game.ball.radius;
        game.ball.vx *= -0.8;
      }
      if (game.ball.x + game.ball.radius > CANVAS_WIDTH) {
        game.ball.x = CANVAS_WIDTH - game.ball.radius;
        game.ball.vx *= -0.8;
      }
      if (game.ball.y - game.ball.radius < 0) {
        game.ball.y = game.ball.radius;
        game.ball.vy *= -0.8;
      }

      // Ball out of bounds
      if (game.ball.y > CANVAS_HEIGHT) {
        setBalls((prev) => {
          const newBalls = prev - 1;
          if (newBalls <= 0) {
            setGameState(GAME_CONFIG.states.GAME_OVER);
          } else {
            game.ball = { x: 150, y: 50, vx: 0, vy: 0, radius: 8 };
          }
          return newBalls;
        });
      }

      // Bumper collisions
      game.bumpers.forEach((bumper) => {
        const dx = game.ball.x - bumper.x;
        const dy = game.ball.y - bumper.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < game.ball.radius + bumper.radius) {
          const angle = Math.atan2(dy, dx);
          game.ball.x = bumper.x + Math.cos(angle) * (bumper.radius + game.ball.radius);
          game.ball.y = bumper.y + Math.sin(angle) * (bumper.radius + game.ball.radius);
          game.ball.vx = Math.cos(angle) * BUMPER_BOOST;
          game.ball.vy = Math.sin(angle) * BUMPER_BOOST;
          setScore((prev) => prev + 100);
        }
      });

      // Flipper collisions (left)
      const leftFlipperEnd = {
        x: game.leftFlipper.x + Math.cos(game.leftFlipper.angle) * game.leftFlipper.length,
        y: game.leftFlipper.y + Math.sin(game.leftFlipper.angle) * game.leftFlipper.length,
      };
      checkFlipperCollision(
        game.ball,
        game.leftFlipper.x,
        game.leftFlipper.y,
        leftFlipperEnd.x,
        leftFlipperEnd.y,
        game.leftFlipper.angle
      );

      // Flipper collisions (right)
      const rightFlipperEnd = {
        x: game.rightFlipper.x + Math.cos(game.rightFlipper.angle) * game.rightFlipper.length,
        y: game.rightFlipper.y + Math.sin(game.rightFlipper.angle) * game.rightFlipper.length,
      };
      checkFlipperCollision(
        game.ball,
        game.rightFlipper.x,
        game.rightFlipper.y,
        rightFlipperEnd.x,
        rightFlipperEnd.y,
        game.rightFlipper.angle
      );

      // Draw walls
      ctx.strokeStyle = GAME_CONFIG.colors.primary;
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, CANVAS_HEIGHT);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(CANVAS_WIDTH, 0);
      ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.stroke();

      // Draw bumpers
      ctx.fillStyle = GAME_CONFIG.colors.danger;
      game.bumpers.forEach((bumper) => {
        ctx.beginPath();
        ctx.arc(bumper.x, bumper.y, bumper.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw flippers
      ctx.strokeStyle = GAME_CONFIG.colors.accent;
      ctx.lineWidth = 8;

      // Left flipper
      ctx.beginPath();
      ctx.moveTo(game.leftFlipper.x, game.leftFlipper.y);
      const leftEnd = {
        x: game.leftFlipper.x + Math.cos(game.leftFlipper.angle) * game.leftFlipper.length,
        y: game.leftFlipper.y + Math.sin(game.leftFlipper.angle) * game.leftFlipper.length,
      };
      ctx.lineTo(leftEnd.x, leftEnd.y);
      ctx.stroke();

      // Right flipper
      ctx.beginPath();
      ctx.moveTo(game.rightFlipper.x, game.rightFlipper.y);
      const rightEnd = {
        x: game.rightFlipper.x + Math.cos(game.rightFlipper.angle) * game.rightFlipper.length,
        y: game.rightFlipper.y + Math.sin(game.rightFlipper.angle) * game.rightFlipper.length,
      };
      ctx.lineTo(rightEnd.x, rightEnd.y);
      ctx.stroke();

      // Draw ball
      ctx.fillStyle = GAME_CONFIG.colors.accent2;
      ctx.beginPath();
      ctx.arc(game.ball.x, game.ball.y, game.ball.radius, 0, Math.PI * 2);
      ctx.fill();

      // Draw HUD
      ctx.fillStyle = GAME_CONFIG.colors.light;
      ctx.font = '14px Arial';
      ctx.fillText(`Balls: ${balls}`, 10, 20);
    }, 30);

    const checkFlipperCollision = (ball, x1, y1, x2, y2, flipperAngle) => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      const nx = -dy / len;
      const ny = dx / len;

      const ballTx = ball.x - x1;
      const ballTy = ball.y - y1;
      const dotProduct = ballTx * nx + ballTy * ny;

      if (Math.abs(dotProduct) < ball.radius) {
        const projLen = ballTx * (dx / len) + ballTy * (dy / len);
        if (projLen >= 0 && projLen <= len) {
          ball.x += nx * (ball.radius - Math.abs(dotProduct));
          ball.y += ny * (ball.radius - Math.abs(dotProduct));
          const boost = 20;
          ball.vx = Math.cos(flipperAngle) * boost;
          ball.vy = Math.sin(flipperAngle) * boost - 5;
          setScore((prev) => prev + 50);
        }
      }
    };

    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, score, highScore, balls]);

  return (
    <GameLayout
      title="Pinball 🎯"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Pinball</h2>
            <p style={styles.menuDescription}>
              Hit the ball with flippers and bump into bumpers for points!
              Z = Left Flipper, M = Right Flipper
            </p>
            <button
              onClick={startGame}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              style={styles.canvas}
            />
            <div style={styles.controls}>Z: Left Flipper | M: Right Flipper</div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button
              onClick={startGame}
              className="btn btn-primary mt-3"
              style={{ width: '100%' }}
            >
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
    display: 'block',
  },
  controls: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default Pinball;
